import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Activity, Clock, TrendingUp, BarChart3, Loader2, RefreshCw, Zap, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatMs = (ms) => {
  if (ms < 1) return '<1ms';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const getLatencyColor = (ms) => {
  if (ms < 100) return 'text-emerald-400';
  if (ms < 300) return 'text-yellow-400';
  if (ms < 1000) return 'text-orange-400';
  return 'text-red-400';
};

const getLatencyBadge = (ms) => {
  if (ms < 100) return { label: 'Fast', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' };
  if (ms < 300) return { label: 'OK', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
  if (ms < 1000) return { label: 'Slow', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };
  return { label: 'Critical', className: 'bg-red-500/20 text-red-400 border-red-500/30' };
};

export const PerformanceDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('24');
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeDetail, setRouteDetail] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/api/metrics/overview?hours=${period}`);
      const result = await res.json();
      setData(result);
    } catch (e) {
      console.error('Failed to load metrics:', e);
    }
    setLoading(false);
    setRefreshing(false);
  }, [period]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const fetchRouteDetail = async (route, method) => {
    try {
      const res = await apiFetch(`${API}/api/metrics/route-detail?route=${encodeURIComponent(route)}&method=${method}&hours=${period}`);
      const result = await res.json();
      setRouteDetail(result);
      setSelectedRoute(`${method} ${route}`);
    } catch (e) {
      console.error('Failed to load route detail:', e);
    }
  };

  const refresh = () => {
    setRefreshing(true);
    fetchMetrics();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  const routes = data?.routes || [];
  const throughput = data?.throughput || [];
  const slowRoutes = [...routes].sort((a, b) => b.p95_ms - a.p95_ms).slice(0, 5);
  const busyRoutes = [...routes].sort((a, b) => b.requests - a.requests).slice(0, 5);

  // Calculate summary stats
  const totalRequests = data?.total_requests || 0;
  const avgLatency = routes.length > 0
    ? routes.reduce((sum, r) => sum + r.avg_ms * r.requests, 0) / Math.max(totalRequests, 1)
    : 0;
  const maxP95 = routes.length > 0 ? Math.max(...routes.map(r => r.p95_ms)) : 0;
  const errorRate = 0; // TODO: calculate from status codes

  return (
    <div className="space-y-6" data-testid="performance-dashboard">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last hour</SelectItem>
              <SelectItem value="6">Last 6h</SelectItem>
              <SelectItem value="24">Last 24h</SelectItem>
              <SelectItem value="72">Last 3 days</SelectItem>
              <SelectItem value="168">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={refresh}
          disabled={refreshing}
          data-testid="refresh-metrics"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Activity className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-400">Total Requests</p>
                <p className="text-xl font-bold" data-testid="total-requests">{totalRequests.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Clock className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-400">Avg Latency</p>
                <p className={`text-xl font-bold ${getLatencyColor(avgLatency)}`} data-testid="avg-latency">
                  {formatMs(avgLatency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Zap className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-400">Max P95</p>
                <p className={`text-xl font-bold ${getLatencyColor(maxP95)}`} data-testid="max-p95">
                  {formatMs(maxP95)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-violet-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-400">Routes Tracked</p>
                <p className="text-xl font-bold" data-testid="routes-tracked">{data?.unique_routes || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mini Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Slowest Routes */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-400" />
              <CardTitle className="text-sm">Slowest Routes (P95)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {slowRoutes.map((r, i) => {
                const badge = getLatencyBadge(r.p95_ms);
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2 rounded bg-zinc-800/30 hover:bg-zinc-800/60 cursor-pointer transition-colors"
                    onClick={() => fetchRouteDetail(r.route, r.method)}
                    data-testid={`slow-route-${i}`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Badge variant="outline" className="text-[10px] px-1.5 shrink-0">{r.method}</Badge>
                      <span className="text-xs text-zinc-300 truncate">{r.route}</span>
                    </div>
                    <Badge variant="outline" className={`text-xs shrink-0 ml-2 ${badge.className}`}>
                      {formatMs(r.p95_ms)}
                    </Badge>
                  </div>
                );
              })}
              {slowRoutes.length === 0 && (
                <p className="text-xs text-zinc-500 text-center py-4">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Most Active Routes */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              <CardTitle className="text-sm">Most Active Routes</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {busyRoutes.map((r, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-2 rounded bg-zinc-800/30 hover:bg-zinc-800/60 cursor-pointer transition-colors"
                  onClick={() => fetchRouteDetail(r.route, r.method)}
                  data-testid={`active-route-${i}`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Badge variant="outline" className="text-[10px] px-1.5 shrink-0">{r.method}</Badge>
                    <span className="text-xs text-zinc-300 truncate">{r.route}</span>
                  </div>
                  <span className="text-xs text-zinc-400 shrink-0 ml-2">{r.requests.toLocaleString()} reqs</span>
                </div>
              ))}
              {busyRoutes.length === 0 && (
                <p className="text-xs text-zinc-500 text-center py-4">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Throughput Chart (simple text-based) */}
      {throughput.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Throughput (requests/min)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-[2px] h-16">
              {throughput.slice(-40).map((t, i) => {
                const maxReqs = Math.max(...throughput.map(x => x.requests), 1);
                const height = Math.max((t.requests / maxReqs) * 100, 4);
                return (
                  <div
                    key={i}
                    className="flex-1 bg-emerald-500/60 rounded-t hover:bg-emerald-400/80 transition-colors"
                    style={{ height: `${height}%` }}
                    title={`${t.time}: ${t.requests} requests`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
              <span>{throughput[Math.max(throughput.length - 40, 0)]?.time?.slice(11) || ''}</span>
              <span>{throughput[throughput.length - 1]?.time?.slice(11) || ''}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Full Route Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">All Routes</CardTitle>
          <CardDescription className="text-xs">Click a row for detailed breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Method</TableHead>
                <TableHead className="text-xs">Route</TableHead>
                <TableHead className="text-xs text-right">Requests</TableHead>
                <TableHead className="text-xs text-right">Avg</TableHead>
                <TableHead className="text-xs text-right">P95</TableHead>
                <TableHead className="text-xs text-right">P99</TableHead>
                <TableHead className="text-xs text-right">Max</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {routes.map((r, i) => (
                <TableRow
                  key={i}
                  className="cursor-pointer hover:bg-zinc-800/50"
                  onClick={() => fetchRouteDetail(r.route, r.method)}
                  data-testid={`route-row-${i}`}
                >
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] px-1.5">{r.method}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-zinc-300 font-mono max-w-[300px] truncate">{r.route}</TableCell>
                  <TableCell className="text-xs text-right">{r.requests.toLocaleString()}</TableCell>
                  <TableCell className={`text-xs text-right font-mono ${getLatencyColor(r.avg_ms)}`}>
                    {formatMs(r.avg_ms)}
                  </TableCell>
                  <TableCell className={`text-xs text-right font-mono ${getLatencyColor(r.p95_ms)}`}>
                    {formatMs(r.p95_ms)}
                  </TableCell>
                  <TableCell className={`text-xs text-right font-mono ${getLatencyColor(r.p99_ms)}`}>
                    {formatMs(r.p99_ms)}
                  </TableCell>
                  <TableCell className={`text-xs text-right font-mono ${getLatencyColor(r.max_ms)}`}>
                    {formatMs(r.max_ms)}
                  </TableCell>
                </TableRow>
              ))}
              {routes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-zinc-500 py-8 text-xs">
                    No metrics collected yet. Make some API calls to see data.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Route Detail Modal */}
      {selectedRoute && routeDetail && (
        <Card className="border-emerald-500/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-mono">{selectedRoute}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedRoute(null); setRouteDetail(null); }}>
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-3 mb-4">
              {['p50', 'p75', 'p90', 'p95', 'p99'].map(p => (
                <div key={p} className="text-center p-2 rounded bg-zinc-800/50">
                  <p className="text-[10px] text-zinc-500 uppercase">{p}</p>
                  <p className={`text-sm font-bold font-mono ${getLatencyColor(routeDetail.percentiles?.[p] || 0)}`}>
                    {formatMs(routeDetail.percentiles?.[p] || 0)}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-400">
              Total: {routeDetail.total_requests?.toLocaleString()} requests over {period}h
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
